import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { RolesService } from '../../../core/services/roles/roles.service';
import { TranslateService } from '@ngx-translate/core';
import { Apollo } from 'apollo-angular';
import { take } from 'rxjs';
import { Order } from 'src/app/order-module/shared/entities/order/order';
import { PageModel, SortModel } from '../../components/task-assignment-list-unassigned/abstract-task-assignment-list';
import { Resource } from '../../shared/entities/resource/resource';
import { ResourceAssignment } from '../../shared/entities/resourceAssignment/resourceAssignment';
import { LocationService } from '../../shared/services/location/location.service';
import { ResourceAssignmentDataSource } from './resourceAssignmentDataSource';

@Component({
  selector: 'app-resource-assignments-list',
  templateUrl: './resource-assignments-list.component.html',
  styleUrls: ['./resource-assignments-list.component.scss']
})
export class ResourceAssignmentsListComponent implements OnDestroy, OnChanges {

  // pagination
  public pageModel: PageModel;
  public defaultPageModel: PageModel = { pageNumber: 0, pageSize : 15 };

  // sorting
  public sortModel: SortModel;
  public sortOptions : SortModel[] = [];
  public defaultSortModel: SortModel;

  // types
  public orderTypesModel: any[] = [];
  public orderTypeOptions = [
    { id: 'RENTAL', name: 'Miete', label: 'Miete' },
    { id: 'OPERATED_RENTAL', name: 'Bedieneinsatz', label: 'Bedieneinsatz' }
  ];
  public defaultOrderTypesModel = undefined; // load all types by default

  // states
  public statesModel: any[] = [];
  public statesOptions = [
    { id: 'NEW', name: 'Neu', label: 'Neu' },
    { id: 'OUT', name: 'Laufend', label: 'Übergeben' },
    { id: 'COMPLETED', name: 'Abgeschlossen', label: 'Abgeschlossen' },
    { id: 'ARCHIVED', name: 'Archiviert', label: 'Archiviert' }
  ];
  public defaultStatesModel = undefined; // load all types by default

  // locations - options will be generated as a result of the locationService.getLocations() call
  public locationsModel: any[] = [];
  public locationsOptions = [];
  public defaultLocationsModel = undefined; // load all locations by default

  dataSource: ResourceAssignmentDataSource;
  loading = false;

  @Input() resource: Resource;

  constructor(
    private apollo: Apollo,
    private locationService: LocationService,
    public rolesService: RolesService,
    private translate: TranslateService

  ) {
    // Initialize translated sortOptions
    this.sortOptions = [
      { label: this.translate.instant('rentModule.resourceAssignmentsListComponent.sortOptions.noSorting'), fieldName: 'id', direction: "asc"},
      { label: this.translate.instant('rentModule.resourceAssignmentsListComponent.sortOptions.startDateAsc'), fieldName: 'outDateTimePeriod', direction: "asc" },
      { label: this.translate.instant('rentModule.resourceAssignmentsListComponent.sortOptions.startDateDesc'), fieldName: 'outDateTimePeriod', direction: "desc" },
      { label: this.translate.instant('rentModule.resourceAssignmentsListComponent.sortOptions.endDateAsc'), fieldName: 'inDateTimePeriod', direction: "asc" },
      { label: this.translate.instant('rentModule.resourceAssignmentsListComponent.sortOptions.endDateDesc'), fieldName: 'inDateTimePeriod', direction: "desc" },
      { label: this.translate.instant('rentModule.resourceAssignmentsListComponent.sortOptions.orderNumberAsc'), fieldName: 'lineItem.order.number', direction: "asc" },
      { label: this.translate.instant('rentModule.resourceAssignmentsListComponent.sortOptions.orderNumberDesc'), fieldName: 'lineItem.order.number', direction: "desc"}
    ];
    this.defaultSortModel = this.sortOptions[0];
    this.locationService.getLocations().pipe(
      take(1)
    ).subscribe(locations => {
      this.locationsOptions = locations;
    });
    // no state currently stored for this page
    this.pageModel = this.defaultPageModel;
    this.sortModel = this.defaultSortModel;
    this.orderTypesModel = this.defaultOrderTypesModel;
    this.statesModel = this.defaultStatesModel;
    this.locationsModel = this.defaultLocationsModel;
    this.dataSource = new ResourceAssignmentDataSource(this.apollo);
    this.dataSource.loading.subscribe((loading) => {
      this.loading = loading;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(this.resource && this.resource.id){
      this.loadResourceAssignments();
    } else {
      this.refetchResourceAssignments();
    }
  }

  ngOnDestroy(): void {
    this.dataSource?.disconnect();
  }

  loadResourceAssignments(){
    this.dataSource?.loadResourceAssignments({
      pageNumber: this.pageModel?.pageNumber,
      pageSize: this.pageModel?.pageSize,
      sortProperty: this.sortModel?.fieldName,
      sortDirection: this.sortModel?.direction,
      orderLocationIds: this.locationsModel?.map(location => location.id),
      orderTypes: this.orderTypesModel?.map(type => type.id),
      states: this.statesModel?.map(state => state.id),
      resourceId: this.resource?.id
    });
  }

  refetchResourceAssignments(force = false): void {
    this.dataSource.refetchQuery({
      pageNumber: this.pageModel?.pageNumber,
      pageSize: this.pageModel?.pageSize,
      sortProperty: this.sortModel?.fieldName,
      sortDirection: this.sortModel?.direction,
      orderLocationIds: this.locationsModel?.map(location => location.id),
      orderTypes: this.orderTypesModel?.map(type => type.id),
      states: this.statesModel?.map(state => state.id),
    }, force);
  }

  public getIcon(resourceAssignment: ResourceAssignment){
    return Order.getOrderIcon(resourceAssignment?.lineItem?.order?.type + "");
  }

  public getState(resourceAssignment: ResourceAssignment){
    return ResourceAssignment.getResourceAssignmentStateStatic(resourceAssignment?.state + "");
  }

  public getSeverity(resourceAssignment: ResourceAssignment){
    return ResourceAssignment.getResourceAssignmentStateSeverity(resourceAssignment?.state + "");
  }

  public getContactName(resourceAssignment: ResourceAssignment){
    return resourceAssignment?.lineItem?.order?.customer?.getFullName() ? resourceAssignment?.lineItem?.order?.customer?.getFullName() : '-';
  }

  onPageChange(event) {
    this.pageModel = { pageNumber: event.page, pageSize: event.rows };
    this.refetchResourceAssignments();
  }

}



